/**
 * Bodies of the pages the platform draws itself (`PlatformSettingBuiltin`, 见 21):
 * Language, Appearance, Shortcuts, Slash Commands, About and Developer options. They sit
 * in the same tree as module sections; only the body comes from here.
 */
import type { ChannelClient } from '@hibro/platform-core';
import type {
  ILauncherService,
  IModuleService,
  INlsService,
  IProductConfigService,
  IThemeService,
  IUpdateService,
  IWhenContextService,
  PlatformSettingBuiltin,
} from '@hibro/shared';
import {
  SETTINGS_CARD,
  SettingsHint,
  SettingsPane,
  SettingsSwitchRow,
} from '@hibro/ui-client';
import type { ReactNode } from 'react';

import { AboutPage } from './about-pages';
import { AppearancePage, LanguagePage } from './general-pages';
import { ShortcutsPage } from './shortcuts-page';
import { SlashCommandsPage } from './slash-page';

export interface BuiltinSectionProps {
  builtin: PlatformSettingBuiltin;
  t: (key: string, args?: Record<string, unknown>) => string;
  moduleService: IModuleService;
  launcherService?: ILauncherService;
  whenContextService?: IWhenContextService;
  client?: ChannelClient;
  nlsService?: INlsService;
  themeService?: IThemeService;
  updateService?: IUpdateService;
  productConfigService?: IProductConfigService;
  openAboutLink: (url: string) => void;
  onAboutLogoTap: () => void;
  /** Old platform settings body shown under Developer options. */
  developerSettings?: ReactNode;
  onDisableDeveloper: () => void;
}

export function BuiltinSection({
  builtin,
  t,
  moduleService,
  launcherService,
  whenContextService,
  client,
  nlsService,
  themeService,
  updateService,
  productConfigService,
  openAboutLink,
  onAboutLogoTap,
  developerSettings,
  onDisableDeveloper,
}: BuiltinSectionProps): ReactNode {
  switch (builtin) {
    case 'language':
      return <LanguagePage t={t} nlsService={nlsService} client={client} />;
    case 'appearance':
      return (
        <AppearancePage t={t} themeService={themeService} client={client} />
      );
    case 'shortcuts':
      return <ShortcutsPage />;
    case 'slash':
      return (
        <SlashCommandsPage
          moduleService={moduleService}
          launcherService={launcherService}
          client={client}
          whenContextService={whenContextService}
        />
      );
    case 'about':
      return (
        <AboutPage
          t={t}
          onLogoTap={onAboutLogoTap}
          updateService={updateService}
          client={client}
          productConfig={productConfigService}
          openExternal={openAboutLink}
        />
      );
    case 'notifications':
      // Drawn by `NotificationsSettings` on the page itself. This section only
      // keeps the page in the tree when no module has contributed a switch.
      return null;
    case 'developer':
      return (
        <SettingsPane>
          <div className={SETTINGS_CARD}>
            <SettingsSwitchRow
              icon="i-bug"
              label={t('settingsWindow.developer.show')}
              checked
              last
              onChange={(next) => {
                if (!next) {
                  onDisableDeveloper();
                }
              }}
            />
          </div>
          {developerSettings ?? (
            <SettingsHint>{t('settingsWindow.unavailable')}</SettingsHint>
          )}
        </SettingsPane>
      );
  }
}
